import debounce from 'debounce';
import rimg from 'rimg-shopify';
import AsyncView from 'shopify-asyncview';
import ScrollLock from '../utils/ScrollLock';
import layout from '../utils/layout';

export default class LiveSearch {
  constructor(section, options = {}) {
    this.el = section.querySelector('[data-search]');
    this.showBorder = options.showBorder;
    this.liveSearchEnabled = options.liveSearchEnabled;
    this.searchResultsDisplay = options.searchResultsDisplay;
    this.searchUrl = options.searchUrl;

    this.minimumCharactersBeforeSearch = 3;

    this.header = document.querySelector('[data-header-container]');
    this.branding = document.querySelector('[data-header-branding]');
    this.navigation = document.querySelector('[data-header-navigation]');
    this.searchTriggers = section.querySelectorAll('[data-header-search-button]');
    this.searchOverlay = this.el.querySelector('[data-search-overlay]');
    this.searchForm = this.el.querySelector('[data-search-form]');
    this.searchBox = this.el.querySelector('[data-search-form-input]');
    this.closeButton = this.el.querySelector('[data-search-form-button-close]');
    this.searchResults = this.el.querySelector('[data-livesearch-results]');
    this.searchResultsWrapper = this.el.querySelector('[data-livesearch-results-wrapper]');
    this.searchResultsProducts = this.el.querySelector('[data-livesearch-products]');
    this.searchResultsPages = this.el.querySelector('[data-livesearch-pages]');
    this.searchResultsViewall = this.el.querySelector('[data-livesearch-viewall]');
    this.resultsCount = this.el.querySelector('[data-livesearch-results-count]');

    this.productsPlaceholder = this.searchResultsProducts.innerHTML;
    this.pagesPlaceholder = this.searchResultsPages.innerHTML;

    this.isTransitioning = false;

    this.stopProp = (e) => {
      e.stopPropagation();
    };

    this.open = () => {
      this.openSearchBar();
    };
    this.close = (e) => {
      e.preventDefault();
      this.closeSearchBar();
    };
    this.closeEsc = (e) => {
      if (e.key === 'Escape') {
        this.stopProp(e);
        this.close(e);
      }
    };
    this.toggle = (e) => {
      e.preventDefault();
      this.stopProp(e);
      this.toggleSearchBar();
    };

    this.searchDebounced = debounce(this._search.bind(this), 500);
    this.resizeDebounced = debounce(this._handleResize.bind(this), 200);

    for (let i = 0; i < this.searchTriggers.length; i++) {
      this.searchTriggers[i].addEventListener('click', this.toggle);
      this.searchTriggers[i].addEventListener('focusin', this.stopProp);
    }

    this.closeButton.addEventListener('click', this.close);
    this.searchForm.addEventListener('click', this.stopProp);
    this.searchForm.addEventListener('focusin', this.stopProp);
    this.searchResults.addEventListener('click', this.stopProp);
    this.searchResults.addEventListener('focusin', this.stopProp);
  }

  toggleSearchBar() {
    if (this.el.classList.contains('visible')) {
      this.closeSearchBar();
    } else {
      this.openSearchBar();
    }
  }

  _placeSearchBar() {
    const [leftSearch, rightSearch] = this.searchTriggers;

    // Set the visible trigger so we know which one to focus on after done searching.
    if (layout.isBreakpoint('L', 'XL')) {
      // If on desktop, the search box is shown directly below the header, but hides the nav
      // if the nav is overflowing and showing below the header.
      if (this.header.classList.contains('header-nav-overflow')
        || this.header.classList.contains('header-layout-traditional')) {
        this.navigation.classList.add('header-nav-hidden');
      }
      const headerBottom = this.branding.getBoundingClientRect().bottom;
      this.searchForm.style.top = this.showBorder ? `${headerBottom + 1}px` : `${headerBottom}px`;
      this.visibleTrigger = this.header.classList.contains('header-search-left') ? leftSearch : rightSearch;
    } else {
      // If on mobile, the search box is shown overtop of the header.
      this.searchForm.style.top = '0px';
      this.visibleTrigger = rightSearch;
    }
  }

  _handleResize() {
    this._placeSearchBar();
    this.searchResults.style.paddingTop = `${this.searchForm.getBoundingClientRect().bottom}px`;
  }

  openSearchBar() {
    if (this.isTransitioning) {
      return;
    }

    this.isTransitioning = true;

    window.addEventListener('keydown', this.closeEsc);
    document.body.addEventListener('click', this.close);
    document.body.addEventListener('focusin', this.close);

    this._placeSearchBar();

    // When placing both the form and the results div, we need to place them dynamically based on
    // the position of other elements. The results div needs to wait for the form to complete
    // animating before we can position it, thus this complicated waiting for animationend.
    this.doneSearchAnimations = () => {
      this.searchResults.style.paddingTop = `${this.searchForm.getBoundingClientRect().bottom}px`;
      this.searchForm.classList.remove('animating-in');
      this.searchForm.classList.add('open');

      this.doneResultsAnimations = () => {
        this.searchResults.classList.remove('animating-in');
        this.searchResults.classList.add('open');
        this.searchResults.removeEventListener('animationend', this.doneResultsAnimations);
        this.isTransitioning = false;
      };

      this.searchResults.addEventListener('animationend', this.doneResultsAnimations);
      this.searchResults.classList.add('visible');
      this.searchResults.classList.add('animating-in');
      this.searchOverlay.classList.remove('animating-in');

      this.searchForm.removeEventListener('animationend', this.doneSearchAnimations);
    };

    this.searchForm.addEventListener('animationend', this.doneSearchAnimations);
    this.searchForm.classList.add('animating-in');
    this.searchOverlay.classList.add('animating-in');

    this.el.classList.add('visible');
    this.searchBox.focus();
    this.searchResultsWrapper.addEventListener('touchmove', this.stopProp);
    ScrollLock.lock();

    if (this.liveSearchEnabled) {
      this.searchBox.addEventListener('keyup', this.searchDebounced);
    }

    window.addEventListener('resize', this.resizeDebounced);
  }

  closeSearchBar() {
    if (this.isTransitioning) {
      return;
    }

    this.isTransitioning = true;

    // The following animation listeners are the opposite of the animations on open, above.
    this.doneResultsAnimations = () => {
      this.searchResults.classList.remove('animating-out');
      this.searchResults.classList.remove('open');
      this.searchResults.classList.remove('visible');

      this.doneSearchAnimations = () => {
        this.searchForm.classList.remove('animating-out');
        this.searchForm.classList.remove('open');
        this.searchForm.removeEventListener('animationend', this.doneSearchAnimations);
        this.el.classList.remove('visible');
        this.searchOverlay.classList.remove('animating-out');
        this.isTransitioning = false;
      };

      this.searchForm.addEventListener('animationend', this.doneSearchAnimations);
      this.searchForm.classList.add('animating-out');

      this.searchResults.removeEventListener('animationend', this.doneResultsAnimations);
    };

    this.navigation.classList.remove('header-nav-hidden');

    this.searchResults.addEventListener('animationend', this.doneResultsAnimations);
    this.searchResults.classList.add('animating-out');
    this.searchOverlay.classList.add('animating-out');

    this.visibleTrigger.focus();
    this.searchResultsWrapper.removeEventListener('touchmove', this.stopProp);
    ScrollLock.unlock();

    window.removeEventListener('keydown', this.closeEsc);
    document.body.removeEventListener('click', this.close);
    document.body.removeEventListener('focusin', this.close);

    this.searchBox.removeEventListener('keyup', this.searchDebounced);

    window.removeEventListener('resize', this.resizeDebounced);
  }

  _search() {
    const searchString = this.searchBox.value;

    if (this.previousSearchString === searchString) {
      return;
    }

    this.searchResults.style.paddingTop = `${this.searchForm.getBoundingClientRect().bottom}px`;

    if (searchString.length >= this.minimumCharactersBeforeSearch) {
      this.searchResultsProducts.innerHTML = this.productsPlaceholder;
      this.searchResultsPages.innerHTML = this.pagesPlaceholder;
      this.searchResults.classList.add('livesearch-results-searching');
      this.searchResults.classList.remove('livesearch-results-empty');
      this.searchResults.classList.remove('livesearch-results-exist');
      this._loadResults(searchString);
    } else {
      this.searchResults.classList.remove('livesearch-results-empty');
      this.searchResults.classList.remove('livesearch-results-exist');
      this.searchResults.classList.remove('livesearch-results-searching');
    }

    this.previousSearchString = searchString;
  }

  _loadResults(searchString) {
    const searchTypes = this.searchResultsDisplay.replace(/_/g, ',')
      .replace('posts', 'article').replace('pages', 'page').replace('products', 'product');
    AsyncView.load(
      `${window.Theme.routes.search_url}?type=${searchTypes}&q=${searchString}`,
      'ajax',
      searchString,
    ).done(({ html, options }) => {
      this.searchResultsProducts.innerHTML = html.products;
      this.searchResultsPages.innerHTML = html.pages;

      this.searchResultsViewall.href = options.search_url;

      // Add a timeout to give rimg a little more time to load placeholder SVG
      // to prevent images from loading at the wrong size in Firefox
      setTimeout(() => rimg.watch(this.searchResults), 1);

      if (layout.isBreakpoint('L')) {
        const headerHeight = this.header.getBoundingClientRect().height;
        const formHeight = this.searchForm.getBoundingClientRect().height;
        this.searchResults.style.height = `${window.innerHeight - headerHeight - formHeight}px`;
      } else {
        this.searchResults.style.height = '';
      }

      this.resultsCount.innerHTML = options.search_results_count
        .toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','); // Add comma delimeter for thousands.

      if (options.search_results_count > 0) {
        this.searchResults.classList.add('livesearch-results-exist');
        this.searchResults.classList.remove('livesearch-results-searching');
        this.searchResults.classList.remove('livesearch-results-empty');
      } else {
        this.searchResults.classList.add('livesearch-results-empty');
        this.searchResults.classList.remove('livesearch-results-exist');
        this.searchResults.classList.remove('livesearch-results-searching');
      }
    });
  }

  unload() {
    this.navigation.classList.remove('header-nav-hidden');

    for (let i = 0; i < this.searchTriggers.length; i++) {
      this.searchTriggers[i].removeEventListener('click', this.toggle);
      this.searchTriggers[i].removeEventListener('focusin', this.stopProp);
    }

    this.closeButton.removeEventListener('click', this.close);
    this.searchForm.removeEventListener('click', this.stopProp);
    this.searchForm.removeEventListener('focusin', this.stopProp);
    this.searchResults.removeEventListener('click', this.stopProp);
    this.searchResults.removeEventListener('focusin', this.stopProp);

    window.removeEventListener('keydown', this.closeEsc);
    window.removeEventListener('resize', this.close);
    document.body.removeEventListener('click', this.close);
    document.body.removeEventListener('focusin', this.close);
  }
}
